'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Download, RefreshCw, TrendingDown, TrendingUp, AlertTriangle, Ship, BarChart3, Users } from 'lucide-react';
import LsdTrendChart from './LsdTrendChart';
import CategoryDeficiencyChart from './CategoryDeficiencyChart';
import SeverityDistributionChart from './SeverityDistributionChart';
import VesselBenchmarkChart from './VesselBenchmarkChart';
import VesselRankingTable from './VesselRankingTable';
import TopFindingsList from './TopFindingsList';
import OwnerPerformanceTable from './OwnerPerformanceTable';
import { VESSEL_BENCHMARK_DATA, TOP_COMMON_FINDINGS, OWNER_PERFORMANCE } from './analyticsData';
import Icon from '@/components/ui/AppIcon';


export default function AnalyticsDashboardClient() {
  const [dateRange, setDateRange] = useState('12m');
  const [scopeFilter, setScopeFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');

  const kpiCards = [
    {
      id: 'kpi-fleet-lsd',
      label: 'Fleet Avg LSD Score',
      value: '43.2',
      subValue: 'per inspection',
      trend: -8.4,
      trendLabel: 'vs prior period',
      trendGood: true,
      icon: BarChart3,
      color: 'hsl(218 70% 22%)',
      bg: 'hsl(218 70% 22% / 0.06)',
      size: 'large',
    },
    {
      id: 'kpi-total-insp',
      label: 'Total Inspections',
      value: '73',
      subValue: 'last 12 months',
      trend: +12,
      trendLabel: 'vs prior period',
      trendGood: true,
      icon: Ship,
      color: 'hsl(218 70% 22%)',
      bg: 'hsl(218 70% 22% / 0.05)',
      size: 'normal',
    },
    {
      id: 'kpi-critical',
      label: 'Critical Findings',
      value: '24',
      subValue: 'open this period',
      trend: +3,
      trendLabel: 'vs prior period',
      trendGood: false,
      icon: AlertTriangle,
      color: 'hsl(355 75% 50%)',
      bg: 'hsl(355 75% 58% / 0.07)',
      size: 'normal',
    },
    {
      id: 'kpi-psc',
      label: 'PSC Detentions',
      value: '2',
      subValue: 'last 12 months',
      trend: 0,
      trendLabel: 'same as prior period',
      trendGood: null,
      icon: AlertTriangle,
      color: 'hsl(38 72% 36%)',
      bg: 'hsl(38 92% 50% / 0.07)',
      size: 'normal',
    },
    {
      id: 'kpi-vessels',
      label: 'Vessels Inspected',
      value: '31',
      subValue: 'distinct vessels',
      trend: +5,
      trendLabel: 'vs prior period',
      trendGood: true,
      icon: Ship,
      color: 'hsl(145 60% 32%)',
      bg: 'hsl(145 60% 40% / 0.07)',
      size: 'normal',
    },
    {
      id: 'kpi-repeat',
      label: 'Repeat Finding Rate',
      value: '34%',
      subValue: 'same deficiency 2+ inspections',
      trend: -6,
      trendLabel: 'vs prior period',
      trendGood: true,
      icon: TrendingDown,
      color: 'hsl(210 80% 38%)',
      bg: 'hsl(210 80% 55% / 0.07)',
      size: 'normal',
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      {/* Page Header */}
      <div className="px-6 py-5 border-b bg-white sticky top-0 z-20" style={{ borderColor: 'hsl(var(--border))' }}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold">Fleet Analytics Dashboard</h1>
            <p className="text-xs text-muted mt-0.5">Anglo American — Miner Inspection Benchmarking</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Date Range */}
            <div className="flex items-center gap-1 rounded-lg border p-1" style={{ borderColor: 'hsl(var(--border))' }}>
              {[
                { val: '3m', label: '3M' },
                { val: '6m', label: '6M' },
                { val: '12m', label: '12M' },
                { val: 'ytd', label: 'YTD' },
              ]?.map(({ val, label }) => (
                <button
                  key={`dr-${val}`}
                  className="px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-150"
                  style={{
                    background: dateRange === val ? 'hsl(var(--primary))' : 'transparent',
                    color: dateRange === val ? '#fff' : 'hsl(var(--muted-foreground))',
                  }}
                  onClick={() => setDateRange(val)}
                >
                  {label}
                </button>
              ))}
            </div>
            {/* Scope Filter */}
            <select
              className="input-field text-xs w-40"
              value={scopeFilter}
              onChange={(e) => setScopeFilter(e?.target?.value)}
            >
              <option value="">All Scopes</option>
              {['Anglo American', 'FMG', 'Rio Tinto', 'Vale']?.map((s) => (
                <option key={`scope-f-${s}`} value={s}>{s}</option>
              ))}
            </select>
            <button className="btn-secondary text-xs" onClick={() => { toast?.info('Refreshing analytics...'); }}>
              <RefreshCw size={14} /> Refresh
            </button>
            <button className="btn-secondary text-xs" onClick={() => toast?.success('Exporting fleet analytics report')}>
              <Download size={14} /> Export Report
            </button>
          </div>
        </div>
      </div>
      <div className="flex-1 px-6 py-6 max-w-screen-2xl mx-auto w-full space-y-6">

        {/* KPI Bento Grid — 6 cards: large (spans 2) + 5 normal in grid-cols-5 */}
        {/* Plan: grid-cols-5 → row1: large(col-span-2) + 3 normal = 5 cols → row2: 2 normal + spacer or merge */}
        {/* Revised: grid-cols-4 → row1: large(col-span-2)+2normal → row2: 4 normal cards */}
        {/* Final: grid-cols-4 → row1: hero(2cols) + 2cards → row2: 4cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
          {/* Hero Card — Fleet Avg LSD */}
          <div className="xl:col-span-2 metric-card" style={{ background: kpiCards?.[0]?.bg, borderColor: 'hsl(218 70% 22% / 0.15)' }}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'hsl(var(--muted-foreground))' }}>
                {kpiCards?.[0]?.label}
              </p>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'hsl(218 70% 22% / 0.12)' }}>
                <BarChart3 size={18} style={{ color: kpiCards?.[0]?.color }} />
              </div>
            </div>
            <div className="flex items-end gap-4">
              <div>
                <p className="text-5xl font-bold tabular-nums" style={{ color: kpiCards?.[0]?.color }}>{kpiCards?.[0]?.value}</p>
                <p className="text-sm text-muted mt-1">{kpiCards?.[0]?.subValue}</p>
              </div>
              <div className="flex-1 h-12 flex items-end pb-1">
                {/* Mini sparkline bars */}
                {[52, 49, 55, 48, 43, 46, 41, 38, 44, 40, 47, 43]?.map((v, i) => (
                  <div
                    key={`spark-${i}`}
                    className="flex-1 mx-px rounded-sm transition-all"
                    style={{
                      height: `${((v - 30) / 30) * 100}%`,
                      background: v > 50 ? 'hsl(355 75% 58% / 0.4)' : 'hsl(218 70% 22% / 0.3)',
                    }}
                  />
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <TrendingDown size={13} style={{ color: 'hsl(var(--success))' }} />
              <span className="text-xs font-semibold" style={{ color: 'hsl(var(--success))' }}>
                {Math.abs(kpiCards?.[0]?.trend)}% improvement
              </span>
              <span className="text-xs text-muted">{kpiCards?.[0]?.trendLabel}</span>
            </div>
          </div>

          {/* Normal cards — row 1 remainder */}
          {kpiCards?.slice(1, 3)?.map((card) => {
            const Icon = card?.icon;
            return (
              <div key={card?.id} className="metric-card" style={{ background: card?.bg, borderColor: `${card?.color}22` }}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted">{card?.label}</p>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${card?.color}18` }}>
                    <Icon size={16} style={{ color: card?.color }} />
                  </div>
                </div>
                <p className="text-3xl font-bold tabular-nums" style={{ color: card?.color }}>{card?.value}</p>
                <div className="flex items-center gap-1.5">
                  {card?.trend > 0 && !card?.trendGood && <TrendingUp size={12} style={{ color: 'hsl(var(--destructive))' }} />}
                  {card?.trend > 0 && card?.trendGood && <TrendingUp size={12} style={{ color: 'hsl(var(--success))' }} />}
                  {card?.trend < 0 && card?.trendGood && <TrendingDown size={12} style={{ color: 'hsl(var(--success))' }} />}
                  {card?.trend < 0 && !card?.trendGood && <TrendingDown size={12} style={{ color: 'hsl(var(--destructive))' }} />}
                  <span className="text-xs font-semibold" style={{
                    color: card?.trendGood === null ? 'hsl(var(--muted-foreground))' :
                      card?.trendGood ? 'hsl(var(--success))' : 'hsl(var(--destructive))',
                  }}>
                    {card?.trend > 0 ? '+' : ''}{card?.trend}{typeof card?.value === 'string' && card?.value?.includes('%') ? 'pp' : ''}
                  </span>
                  <span className="text-xs text-muted truncate">{card?.trendLabel}</span>
                </div>
              </div>
            );
          })}

          {/* Row 2 — 4 normal cards filling the 4-col grid */}
          {kpiCards?.slice(3)?.map((card) => {
            const Icon = card?.icon;
            return (
              <div key={card?.id} className="metric-card" style={{ background: card?.bg, borderColor: `${card?.color}22` }}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted">{card?.label}</p>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${card?.color}18` }}>
                    <Icon size={16} style={{ color: card?.color }} />
                  </div>
                </div>
                <p className="text-3xl font-bold tabular-nums" style={{ color: card?.color }}>{card?.value}</p>
                <div className="flex items-center gap-1.5">
                  {card?.trend > 0 && !card?.trendGood && <TrendingUp size={12} style={{ color: 'hsl(var(--destructive))' }} />}
                  {card?.trend > 0 && card?.trendGood && <TrendingUp size={12} style={{ color: 'hsl(var(--success))' }} />}
                  {card?.trend < 0 && card?.trendGood && <TrendingDown size={12} style={{ color: 'hsl(var(--success))' }} />}
                  {card?.trend < 0 && !card?.trendGood && <TrendingDown size={12} style={{ color: 'hsl(var(--destructive))' }} />}
                  {card?.trend === 0 && <span className="text-xs text-muted">—</span>}
                  {card?.trend !== 0 && (
                    <span className="text-xs font-semibold" style={{
                      color: card?.trendGood === null ? 'hsl(var(--muted-foreground))' :
                        card?.trendGood ? 'hsl(var(--success))' : 'hsl(var(--destructive))',
                    }}>
                      {card?.trend > 0 ? '+' : ''}{card?.trend}{typeof card?.value === 'string' && card?.value?.includes('%') ? 'pp' : ''}
                    </span>
                  )}
                  <span className="text-xs text-muted truncate">{card?.trendLabel}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Charts Row 1 — LSD Trend (large) + Severity Distribution */}
        <div className="grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 bg-white rounded-xl border shadow-card p-5" style={{ borderColor: 'hsl(var(--border))' }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-sm">Fleet LSD Score Trend</h3>
                <p className="text-xs text-muted">Average LSD score and inspection volume over 12 months</p>
              </div>
              <span className="badge badge-normal text-xs">12-month view</span>
            </div>
            <LsdTrendChart />
          </div>
          <div className="bg-white rounded-xl border shadow-card p-5" style={{ borderColor: 'hsl(var(--border))' }}>
            <div className="mb-4">
              <h3 className="font-semibold text-sm">Severity Distribution</h3>
              <p className="text-xs text-muted">All findings by severity rating</p>
            </div>
            <SeverityDistributionChart />
          </div>
        </div>

        {/* Charts Row 2 — Category Deficiencies (large) + Vessel Benchmark */}
        <div className="grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 bg-white rounded-xl border shadow-card p-5" style={{ borderColor: 'hsl(var(--border))' }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-sm">Deficiency Frequency by Category</h3>
                <p className="text-xs text-muted">Number of findings per inspection category across all vessels</p>
              </div>
            </div>
            <CategoryDeficiencyChart />
          </div>
          <div className="bg-white rounded-xl border shadow-card p-5" style={{ borderColor: 'hsl(var(--border))' }}>
            <div className="mb-4">
              <h3 className="font-semibold text-sm">Vessel LSD Benchmark</h3>
              <p className="text-xs text-muted">Total LSD score ranking — lower is better</p>
            </div>
            <VesselBenchmarkChart />
          </div>
        </div>

        {/* Bottom Data Row — Top Findings + Vessel Ranking + Owner Performance */}
        <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border shadow-card p-5" style={{ borderColor: 'hsl(var(--border))' }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-sm">Top 10 Common Findings</h3>
                <p className="text-xs text-muted">Deficiencies appearing across most vessels</p>
              </div>
              <span className="badge badge-critical text-xs">Fleet-wide risk</span>
            </div>
            <TopFindingsList findings={TOP_COMMON_FINDINGS} />
          </div>
          <div className="bg-white rounded-xl border shadow-card p-5" style={{ borderColor: 'hsl(var(--border))' }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-sm">Vessel LSD Ranking</h3>
                <p className="text-xs text-muted">All inspected vessels ranked by total LSD score</p>
              </div>
            </div>
            <VesselRankingTable vessels={VESSEL_BENCHMARK_DATA} />
          </div>
          <div className="xl:col-span-2 2xl:col-span-1 bg-white rounded-xl border shadow-card p-5" style={{ borderColor: 'hsl(var(--border))' }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-sm">Owner Performance</h3>
                <p className="text-xs text-muted">Average LSD score by ship owner</p>
              </div>
              <Users size={16} className="text-muted" />
            </div>
            <OwnerPerformanceTable owners={OWNER_PERFORMANCE} />
          </div>
        </div>
      </div>
    </div>
  );
}